import { useEffect, useRef, useState } from "react";
import { useExperience } from "../../context/ExperienceContext";
import { Icons } from "../../icons/icons";
import "./ExperienceWelcomeModal.css";

const WELCOME_STORAGE_KEY = "experience-welcome-seen";
const AUTO_SELECT_DELAY = 2 * 60 * 1000; // 2 minutos
const LOGO_SRC = "/assets/images/logo/Logo.png";

const welcomeOptions = [
  {
    value: "all",
    number: "01",
    eyebrow: "Experiência completa",
    title: (
      <>
        Estética
        <br />+ Nutrição
      </>
    ),
    description:
      "Todos os cuidados que unem estética, saúde, bem-estar e acompanhamento nutricional.",
    icon: "HeartHandshake",
    image:
      "https://images.unsplash.com/photo-1512316609839-ce289d3eba0a?auto=format&fit=crop&w=900&q=80",
  },
  {
    value: "nutrition",
    number: "02",
    eyebrow: "Foco em saúde",
    title: "Nutrição",
    description:
      "Foque em alimentação, equilíbrio, emagrecimento com leveza, saúde da mulher e acompanhamento nutricional.",
    icon: "leaf",
    image:
      "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=900&q=80",
  },
  {
    value: "aesthetics",
    number: "03",
    eyebrow: "Foco em autoestima",
    title: "Estética",
    description:
      "Procedimentos estéticos, cuidados com a pele, autoestima e protocolos personalizados.",
    icon: "sparkles",
    image:
      "https://images.unsplash.com/photo-1600334129128-685c5582fd35?auto=format&fit=crop&w=900&q=80",
  },
];

function getOptionIcon(iconKey) {
  const iconMap = {
    sparkles: Icons.Sparkles,
    leaf: Icons.Leaf,
    heart: Icons.Heart,
    hearthandshake: Icons.HeartHandshake,
  };

  const normalizedIconKey = String(iconKey || "").toLowerCase();

  return iconMap[normalizedIconKey] || Icons.Sparkles;
}

function hasUserAlreadySeenWelcome() {
  if (typeof window === "undefined") return true;

  return window.localStorage.getItem(WELCOME_STORAGE_KEY) === "true";
}

export default function ExperienceWelcomeModal({
  enabled = true,
  openDelay = 700,
  onComplete,
}) {
  const { setModeWithTransition } = useExperience();

  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const autoSelectTimerRef = useRef(null);
  const hasChosenRef = useRef(false);

  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    if (hasUserAlreadySeenWelcome()) return;

    openTimerRef.current = window.setTimeout(() => {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
    }, openDelay);

    return () => {
      if (openTimerRef.current) {
        window.clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }

      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      if (autoSelectTimerRef.current) {
        window.clearTimeout(autoSelectTimerRef.current);
        autoSelectTimerRef.current = null;
      }

      document.body.style.overflow = "";
    };
  }, [enabled, openDelay]);

  const closeModal = () => {
    setIsClosing(true);

    if (autoSelectTimerRef.current) {
      window.clearTimeout(autoSelectTimerRef.current);
      autoSelectTimerRef.current = null;
    }

    closeTimerRef.current = window.setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      document.body.style.overflow = "";
      closeTimerRef.current = null;

      onComplete?.();
    }, 340);
  };

  const handleChoose = (nextMode, event) => {
    if (hasChosenRef.current) return;

    hasChosenRef.current = true;

    if (autoSelectTimerRef.current) {
      window.clearTimeout(autoSelectTimerRef.current);
      autoSelectTimerRef.current = null;
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem(WELCOME_STORAGE_KEY, "true");
    }

    const rect = event?.currentTarget?.getBoundingClientRect();

    setModeWithTransition(nextMode, {
      x: rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
      y: rect ? rect.top + rect.height / 2 : window.innerHeight / 2,
    });

    closeModal();
  };

  useEffect(() => {
    if (!isVisible) return;

    autoSelectTimerRef.current = window.setTimeout(() => {
      handleChoose("aesthetics");
    }, AUTO_SELECT_DELAY);

    return () => {
      if (autoSelectTimerRef.current) {
        window.clearTimeout(autoSelectTimerRef.current);
        autoSelectTimerRef.current = null;
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        handleChoose("all");
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      className={`experience-welcome ${isClosing ? "is-closing" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="experience-welcome-title"
      aria-describedby="experience-welcome-description"
    >
      <div className="experience-welcome__overlay" />

      <div className="experience-welcome__dialog">
        <span
          className="experience-welcome__brand-mark"
          aria-label="Clínica Dionara Krause"
        >
          <img src={LOGO_SRC} alt="Clínica Dionara Krause" />
        </span>

        <span className="experience-welcome__brand-watermark" aria-hidden="true">
          <img src={LOGO_SRC} alt="" />
        </span>

        <div className="experience-welcome__line-wrap">
          <span />
          <strong>Experiência personalizada</strong>
          <span />
        </div>

        <div className="experience-welcome__header">
          <span className="experience-welcome__badge">
            <Icons.Sparkles />
            Escolha seu foco
          </span>

          <h2 id="experience-welcome-title">
            Qual <em>cuidado</em> combina
            <br />
            com <em>você</em> hoje?
          </h2>

          <p id="experience-welcome-description">
            Vamos adaptar o site para mostrar conteúdos, serviços e mensagens
            mais alinhados com o que você procura.
          </p>
        </div>

        <div className="experience-welcome__options">
          {welcomeOptions.map((option) => {
            const OptionIcon = getOptionIcon(option.icon);

            return (
              <button
                key={option.value}
                type="button"
                className={`experience-welcome__option experience-welcome__option--${option.value}`}
                onClick={(event) => handleChoose(option.value, event)}
                aria-label={`Selecionar ${option.eyebrow}`}
              >
                <img
                  src={option.image}
                  alt=""
                  className="experience-welcome__option-image"
                  loading="eager"
                />

                <span className="experience-welcome__option-shade" />

                <span className="experience-welcome__option-number">
                  {option.number}
                </span>

                <span className="experience-welcome__option-mini-line" />

                <span className="experience-welcome__option-icon">
                  <OptionIcon />
                </span>

                <span className="experience-welcome__option-content">
                  <small>{option.eyebrow}</small>
                  <strong>{option.title}</strong>
                  <span>{option.description}</span>
                </span>

                <span className="experience-welcome__option-action">
                  <Icons.ArrowRight />
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="experience-welcome__skip"
          onClick={(event) => handleChoose("all", event)}
        >
          <Icons.HeartHandshake />
          Prefiro explorar todas as experiências
        </button>
      </div>
    </div>
  );
}